/**
 * Persists the WhatsApp Business API connection retry state per workspace,
 * shared between the Onboarding "Conecta WhatsApp" step and the Dashboard
 * Integrations "Conectar WhatsApp" modal.
 *
 * Migration: an earlier version of Onboarding wrote to
 * `onboarding_v2_wapistate_<workspaceId>` without a timestamp. On first read
 * we copy any pre-existing payload into the new key with
 * `lastAttemptAt = Date.now()` so in-flight sessions don't lose their retry
 * counter, and we leave the old key intact so legacy tabs keep functioning
 * until they reload.
 */

import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import type { tEmbeddedSignupErrorReport } from 'src/hooks/whatsapp/whatsappConnect.types';

const RETRY_TTL_MS = 24 * 60 * 60 * 1000;
const NEW_KEY_PREFIX = 'wapi_state_';
const LEGACY_KEY_PREFIX = 'onboarding_v2_wapistate_';

export type tPersistedPhase =
  | 'connect'
  | 'failed_error'
  | 'failed_closed'
  | 'skipped';

export type tPersistedWapiState = {
  phase: tPersistedPhase;
  retryCount: number;
  hasSkippedBefore: boolean;
  lastAttemptAt: number;
  /**
   * The cause of the last failed attempt, kept so a failure screen restored
   * after a reload or a remount still names it. Written only for a failed
   * attempt and dropped on read when its shape is not the expected one.
   */
  errorCode?: string;
  signupStep?: string;
  signupErrorReport?: tEmbeddedSignupErrorReport;
  /** The Embedded Signup flow the last attempt launched, so a retry reuses it. */
  path?: tEmbeddedSignupPath;
};

export function getWapiStateKey(workspaceId: string): string {
  return `${NEW_KEY_PREFIX}${workspaceId}`;
}

function getLegacyWapiStateKey(workspaceId: string): string {
  return `${LEGACY_KEY_PREFIX}${workspaceId}`;
}

function parsePayload(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

function isValidPhase(value: unknown): value is tPersistedPhase {
  return (
    value === 'connect' ||
    value === 'failed_error' ||
    value === 'failed_closed' ||
    value === 'skipped'
  );
}

function isEmbeddedSignupPath(value: unknown): value is tEmbeddedSignupPath {
  return value === 'coexistence' || value === 'cloud_api';
}

function readSignupErrorReport(
  value: unknown,
): tEmbeddedSignupErrorReport | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return undefined;
  const { code, message } = value as Record<string, unknown>;
  if (code !== null && typeof code !== 'number') return undefined;
  if (message !== null && typeof message !== 'string') return undefined;
  return { code, message };
}

/**
 * Reads the optional cause and path fields one by one, so an invalid field is
 * dropped without discarding its valid siblings or the entry itself.
 */
function readFailureDetails(
  raw: Record<string, unknown>,
): Pick<
  tPersistedWapiState,
  'errorCode' | 'signupStep' | 'signupErrorReport' | 'path'
> {
  const signupErrorReport = readSignupErrorReport(raw.signupErrorReport);
  return {
    ...(typeof raw.errorCode === 'string' ? { errorCode: raw.errorCode } : {}),
    ...(typeof raw.signupStep === 'string'
      ? { signupStep: raw.signupStep }
      : {}),
    ...(signupErrorReport ? { signupErrorReport } : {}),
    ...(isEmbeddedSignupPath(raw.path) ? { path: raw.path } : {}),
  };
}

function normalizePayload(
  raw: Record<string, unknown>,
  fallbackTimestamp: number,
): tPersistedWapiState | null {
  const phase = raw.phase === 'escalated' ? 'failed_closed' : raw.phase;
  if (!isValidPhase(phase)) return null;
  return {
    phase,
    retryCount: typeof raw.retryCount === 'number' ? raw.retryCount : 0,
    hasSkippedBefore: Boolean(raw.hasSkippedBefore),
    lastAttemptAt: typeof raw.lastAttemptAt === 'number' ? raw.lastAttemptAt : fallbackTimestamp,
    ...readFailureDetails(raw),
  };
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    /* access denied — callers treat it as no stored state */
    return null;
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* access denied — best-effort cleanup */
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    return;
  }
}

export function readWapiState(workspaceId: string): tPersistedWapiState | null {
  if (!workspaceId) return null;
  const newKey = getWapiStateKey(workspaceId);
  const now = Date.now();

  let currentRaw: string | null;
  try {
    currentRaw = localStorage.getItem(newKey);
  } catch {
    // Unreadable is not absent: falling through would migrate the legacy entry over the current one.
    return null;
  }
  const fromNew = parsePayload(currentRaw);
  if (fromNew) {
    const normalized = normalizePayload(fromNew, now);
    if (!normalized) {
      safeRemove(newKey);
      return null;
    }
    if (now - normalized.lastAttemptAt >= RETRY_TTL_MS) {
      safeRemove(newKey);
      return null;
    }
    return normalized;
  }

  // Migration path: pull from the legacy onboarding-only key once.
  const fromLegacy = parsePayload(safeGet(getLegacyWapiStateKey(workspaceId)));
  if (!fromLegacy) return null;
  const migrated = normalizePayload(fromLegacy, now);
  if (!migrated) return null;
  // Stamp lastAttemptAt = now so the migrated user gets the full 24h window
  // starting from this read — we don't know when the original attempts were.
  const stamped: tPersistedWapiState = { ...migrated, lastAttemptAt: now };
  safeSet(newKey, JSON.stringify(stamped));
  return stamped;
}

export type tWriteWapiStateInput = Omit<tPersistedWapiState, 'lastAttemptAt'> & {
  /**
   * Optional explicit timestamp. When omitted (or 0), defaults to Date.now()
   * — this preserves the previous behavior for callers that only want to
   * stamp "I just wrote this". The hook's persist-on-mount effect passes
   * the in-memory state.lastAttemptAt so a restored entry does NOT refresh
   * the 24h TTL window every time the modal mounts.
   */
  lastAttemptAt?: number;
};

export function writeWapiState(workspaceId: string, state: tWriteWapiStateInput): void {
  if (!workspaceId) return;
  const lastAttemptAt =
    state.lastAttemptAt && state.lastAttemptAt > 0 ? state.lastAttemptAt : Date.now();
  const payload: tPersistedWapiState = {
    phase: state.phase,
    retryCount: state.retryCount,
    hasSkippedBefore: state.hasSkippedBefore,
    lastAttemptAt,
    ...readFailureDetails(state),
  };
  safeSet(getWapiStateKey(workspaceId), JSON.stringify(payload));
}

export function clearWapiState(workspaceId: string): void {
  if (!workspaceId) return;
  safeRemove(getWapiStateKey(workspaceId));
}
